import { Children, Fragment, isValidElement, type ReactElement, type ReactNode } from 'react';

export type SelectOptionItem = { value: string; label: string; disabled?: boolean };

function extractLabel(node: ReactNode): string {
  if (node == null) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractLabel).join('');
  if (isValidElement(node)) {
    const el = node as ReactElement<{ children?: ReactNode }>;
    return extractLabel(el.props.children);
  }
  return '';
}

function isOptionElement(child: ReactElement): boolean {
  return typeof child.type === 'string' && child.type === 'option';
}

function isFragmentElement(child: ReactElement): boolean {
  return child.type === Fragment;
}

function isOptGroupElement(child: ReactElement): boolean {
  return typeof child.type === 'string' && child.type === 'optgroup';
}

function collectSelectOptions(children: ReactNode, items: SelectOptionItem[]) {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    if (isFragmentElement(child) || isOptGroupElement(child)) {
      collectSelectOptions((child.props as { children?: ReactNode }).children, items);
      return;
    }

    if (!isOptionElement(child)) return;

    const p = child.props as {
      value?: string | number;
      disabled?: boolean;
      children?: ReactNode;
    };
    items.push({
      value: String(p.value ?? ''),
      label: extractLabel(p.children) || String(p.value ?? ''),
      disabled: p.disabled,
    });
  });
}

/** Extrait les &lt;option&gt; même si le bundler transforme les enfants ou les enveloppe dans un fragment. */
export function parseSelectOptions(children: ReactNode): SelectOptionItem[] {
  const items: SelectOptionItem[] = [];
  collectSelectOptions(children, items);
  return items;
}
